import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnimatedSection from '../components/AnimatedSection';
import StaggeredList from '../components/StaggeredList';
import { Card, CardContent } from '../components/ui/card';
import { Users, Award, Heart, Target } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Patient-Centered",
      description: "Every innovation starts with improving patient outcomes and safety"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Nurse-Driven",
      description: "Built by nurses who understand the daily challenges in hospital environments"
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "Evidence-Driven",
      description: "Built on published research about line management and tested with bedside nurses"
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Results-Focused",
      description: "Focused on practical improvements in line organization and nurse workflow"
    }
  ];

  const timeline = [
    { year: "2020", event: "Company founded by ICU nurses" },
    { year: "2021", event: "First Cordflex® prototypes developed" },
    { year: "2022", event: "Clinical trials begin" },
    { year: "2023", event: "FDA establishment registration and device listing" },
    { year: "2024", event: "Commercial launch and expansion" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#214140' }}>
                  About Nursetech Medical™
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  We're a team of nurses and engineers dedicated to creating innovative solutions
                  that improve patient care and make healthcare professionals' lives easier. Founded in 2021,
                  we've been focused on solving real-world problems across the hospital.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20" style={{ backgroundColor: '#DFEAF0' }}>
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
                  Our Mission
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  To empower healthcare professionals with innovative, evidence-based solutions that enhance 
                  patient safety, improve clinical outcomes, and streamline workflows in critical care environments.
                </p>
              </div>
            </AnimatedSection>

            <StaggeredList delay={200} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <Card key={index} className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-full" style={{ backgroundColor: '#8BBAB8', color: 'white' }}>
                        {value.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: '#214140' }}>
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </StaggeredList>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl lg:text-4xl font-bold mb-12 text-center" style={{ color: '#214140' }}>
                  Our Story
                </h2>
                <div className="flex flex-col lg:flex-row items-center gap-12">
                  <div className="flex-1">
                    <p className="text-lg text-gray-600 leading-relaxed mb-6">
                      Nursetech Medical was founded by David, a medical device engineer who partnered with an ICU nurse who spent years watching the same frustrating problem play out every shift: patients tethered to a tangle of IV lines, monitoring cables, and tubes, with no good way to manage them. After COVID pushed nursing to its breaking point, they decided to do something about it.
                    </p>
                    <p className="text-lg text-gray-600 leading-relaxed mb-6">
                      They spent two years listening — conducting structured interviews through product development and entrepreneurship programs at the Johns Hopkins University and speaking with hundreds of nurses, clinical leaders, and care teams across the country. The feedback was clear, consistent, and urgent. So after many iterations between various products, they built the Cordflex: a simple, one-handed, spring-loaded line management clip designed specifically for the critical care bedside. Tested by nurses. Refined by nurses. Trusted by nurses.
                    </p>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Today, Nursetech Medical is on a mission to remove friction from the bedside — giving nurses back time, reducing patient safety risks, increasing hard cost savings across hospital floors, and proving that often small tools make the biggest difference in critical care.
                    </p>
                  </div>
                  <div className="lg:w-2/5 flex-shrink-0">
                    <img
                      src="/images/products/White Cordflex with Stethascope 2.jpeg"
                      alt="White Cordflex clip with stethoscope"
                      className="rounded-xl shadow-lg w-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>


        {/* Timeline Section
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
                  Our Journey
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  From concept to commercial success, here's how we've grown
                </p>
              </div>
            </AnimatedSection>

            <div className="max-w-3xl mx-auto">
              <StaggeredList delay={150} className="space-y-8">
                {timeline.map((item, index) => (
                  <div key={index} className="flex items-center space-x-6">
                    <div className="flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-lg"
                         style={{ backgroundColor: '#214140' }}>
                      {item.year}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg text-gray-700 font-medium">{item.event}</p>
                    </div>
                  </div>
                ))}
              </StaggeredList>
            </div>
          </div>
        </section>
*/}


        {/* Timeline Section
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
                  Our Journey
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  From concept to commercial success, Here's our story
                </p>
              </div>
            </AnimatedSection>

            <div className="max-w-3xl mx-auto">
              <StaggeredList delay={150} className="space-y-8">
                {timeline.map((item, index) => (
                  <div key={index} className="flex items-center space-x-6">
                    <div className="flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-lg"
                         style={{ backgroundColor: '#214140' }}>
                      {item.year}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg text-gray-700 font-medium">{item.event}</p>
                    </div>
                  </div>
                ))}
              </StaggeredList>
            </div>
          </div>
        </section>

Nursetech Medical™ was founded by David, a medical engineer, and an ICU nurse who had spent years watching the same frustrating problem play out every shift: patients tethered to a tangle of IV lines, monitoring cables, and tubes, with no good way to manage them. After COVID pushed nursing to its breaking point, they decided to do something about it.
They spent two years listening — conducting structured interviews through the Johns Hopkins I-CORPS program and speaking with over 200 nurses, clinical leaders, and care teams across the country. The feedback was consistent and urgent. So they built Cordflex: a simple, one-handed, spring-loaded line management clip designed specifically for the ICU bedside. Tested by nurses. Refined by nurses. Trusted by nurses.
Today, Nursetech Medical™ is on a mission to remove friction from the bedside — giving nurses back time, reducing patient safety risks, and proving that the smallest tools can make the biggest difference in critical care.
*/}


        {/* Team Section */}
        <section className="py-20" style={{ backgroundColor: '#273139' }}>
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-white">
                  Leadership Team
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                  Meet the experienced professionals driving innovation in healthcare
                </p>
              </div>
            </AnimatedSection>

            <StaggeredList delay={200} className="grid md:grid-cols-3 gap-8 text-center">
              {[
                { name: "David Usevitch, Ph.D.", role: "CEO & Co-Founder", background: "Biomedical & Medical Device Engineer", image: "/images/team/DavidU-img.png" },
                { name: "Jason Troutner", role: "Business Development Advisor", background: "Medical Device Company Founder and Entrepreneur", image: "/images/team/Jason-Troutner.png" },
                { name: "Jeffrey Cutter", role: "Sales Advisor and Trainer", background: "Expert in Medical Device Sales (Baxter, ICU Medical, FK)", image: "/images/team/jeffcutter.jpeg" }
              ].map((member, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-lg">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#214140' }}>
                    {member.name}
                  </h3>
                  <p className="text-gray-600 font-medium mb-2">{member.role}</p>
                  <p className="text-gray-500 text-sm">{member.background}</p>
                </div>
              ))}
            </StaggeredList>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;